import React, { useEffect, useState } from 'react';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Checkbox from '../../components/common/Checkbox';

const TYPES = [
  { value: 'DISCOUNT_PERCENT', label: 'Desconto percentual (%)' },
  { value: 'DISCOUNT_FIXED', label: 'Desconto fixo (R$)' },
  { value: 'FREE_SHIPPING', label: 'Frete grátis' }
];

const empty = {
  name: '',
  type: 'DISCOUNT_PERCENT',
  value: '',
  code: '',
  minOrderValue: '',
  maxUses: '',
  startsAt: '',
  expiresAt: '',
  isActive: true
};

function toLocalInput(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  // Format yyyy-MM-ddTHH:mm pra <input type=datetime-local>.
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

const CampaignFormModal = ({ isOpen, onClose, onSubmit, initial }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setForm({
        name: initial.name || '',
        type: initial.type || 'DISCOUNT_PERCENT',
        value: initial.value != null ? String(initial.value) : '',
        code: initial.code || '',
        minOrderValue: initial.minOrderValue != null ? String(initial.minOrderValue) : '',
        maxUses: initial.maxUses != null ? String(initial.maxUses) : '',
        startsAt: toLocalInput(initial.startsAt),
        expiresAt: toLocalInput(initial.expiresAt),
        isActive: !!initial.isActive
      });
    } else {
      setForm(empty);
    }
  }, [isOpen, initial]);

  const isFreeShipping = form.type === 'FREE_SHIPPING';

  const handleChange = (field) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: v }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        type: form.type,
        value: isFreeShipping ? 0 : Number(form.value || 0),
        code: form.code.trim() || null,
        minOrderValue: form.minOrderValue ? Number(form.minOrderValue) : null,
        maxUses: form.maxUses ? Number(form.maxUses) : null,
        startsAt: toIso(form.startsAt),
        expiresAt: toIso(form.expiresAt),
        isActive: form.isActive
      });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = form.name.trim() && (isFreeShipping || Number(form.value) > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initial?.id ? 'Editar campanha' : 'Nova campanha'}
      size="md"
      footer={
        <>
          <Button variant="subtle" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={loading} disabled={!canSubmit}>
            {initial?.id ? 'Salvar alterações' : 'Criar campanha'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label="Nome" required>
          <Input
            value={form.name}
            onChange={handleChange('name')}
            placeholder="Ex: Black Friday 25% off"
            maxLength={120}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Tipo" required>
            <Select value={form.type} onChange={handleChange('type')} options={TYPES} />
          </FormField>
          {!isFreeShipping && (
            <FormField
              label={form.type === 'DISCOUNT_PERCENT' ? 'Percentual' : 'Valor (R$)'}
              required
            >
              <Input
                type="number"
                min="0"
                step={form.type === 'DISCOUNT_PERCENT' ? '1' : '0.01'}
                value={form.value}
                onChange={handleChange('value')}
                placeholder={form.type === 'DISCOUNT_PERCENT' ? '10' : '50.00'}
              />
            </FormField>
          )}
        </div>

        <FormField label="Código do cupom" helpText="Opcional. Sem código, a campanha é automática.">
          <Input
            value={form.code}
            onChange={(e) => handleChange('code')({ target: { value: e.target.value.toUpperCase() } })}
            placeholder="Ex: BLACK25"
            maxLength={40}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Pedido mínimo (R$)" helpText="Opcional.">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.minOrderValue}
              onChange={handleChange('minOrderValue')}
              placeholder="100.00"
            />
          </FormField>
          <FormField label="Limite de usos" helpText="Opcional.">
            <Input
              type="number"
              min="1"
              value={form.maxUses}
              onChange={handleChange('maxUses')}
              placeholder="100"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Início">
            <Input type="datetime-local" value={form.startsAt} onChange={handleChange('startsAt')} />
          </FormField>
          <FormField label="Expira em">
            <Input type="datetime-local" value={form.expiresAt} onChange={handleChange('expiresAt')} />
          </FormField>
        </div>

        <div className="flex items-center justify-between p-3 rounded-md bg-app-surface-2">
          <div>
            <p className="font-medium text-app-text">Campanha ativa</p>
            <p className="text-xs text-app-text-secondary">
              Desligar para pausar sem apagar o histórico.
            </p>
          </div>
          <Checkbox
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CampaignFormModal;
